/**
 * UserDetailPanel - Detailed activity breakdown for a selected user
 */

import React, { useState } from 'react';
import { UserActivityDetails, BureauUser } from '../types/bureau-activity.types';
import { ActivityLineChart } from './ActivityLineChart';
import { useUserPlanning } from '../hooks/useBureauActivity';
import {
  ChartBarIcon,
  CursorArrowRaysIcon,
  ClockIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface UserDetailPanelProps {
  user: BureauUser | undefined;
  details: UserActivityDetails | null;
  loading: boolean;
  onClose: () => void;
}

// Tab icons/colors for visual distinction
const tabStyles: Record<string, { color: string; bgColor: string }> = {
  'pipeline': { color: 'text-blue-600', bgColor: 'bg-blue-100' },
  'tracker': { color: 'text-purple-600', bgColor: 'bg-purple-100' },
  'mes-taches': { color: 'text-green-600', bgColor: 'bg-green-100' },
  'emails': { color: 'text-orange-600', bgColor: 'bg-orange-100' },
  'paiement': { color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
  'reunion-lundi': { color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
  'ticket': { color: 'text-rose-600', bgColor: 'bg-rose-100' }
};

export const UserDetailPanel: React.FC<UserDetailPanelProps> = ({
  user,
  details,
  loading,
  onClose
}) => {
  const [visibleTabs, setVisibleTabs] = useState<Set<string>>(new Set([
    'pipeline', 'tracker', 'mes-taches', 'emails', 'paiement', 'reunion-lundi', 'ticket'
  ]));
  const [showFilter, setShowFilter] = useState(false);

  // Fetch user planning/schedule
  const { planning, loading: planningLoading } = useUserPlanning(user?.id || null);

  if (!user) return null;

  const toggleTab = (key: string) => {
    const newVisible = new Set(visibleTabs);
    if (newVisible.has(key)) {
      newVisible.delete(key);
    } else {
      newVisible.add(key);
    }
    setVisibleTabs(newVisible);
  };

  const filteredTabs = details?.byTab.filter(tab => visibleTabs.has(tab.key)) || [];
  const totalRequests = filteredTabs.reduce((sum, tab) => sum + tab.requestCount, 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-lg font-semibold text-blue-600">
              {user.name.split(' ').map(n => n[0]).join('')}
            </span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{user.name}</h3>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : details ? (
        <div className="space-y-6">
          {/* Action Count (Smart Count) */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <CursorArrowRaysIcon className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-600">Actions distinctes</span>
            </div>
            <div className="text-3xl font-bold text-blue-600">{details.actionCount}</div>
            <p className="text-xs text-gray-500 mt-1">
              Groupées par intervalles de 5 secondes
            </p>
          </div>

          {/* Activity Timeline with Work Schedule */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <ClockIcon className="w-4 h-4" />
              Activité dans le temps (intervalles de 5 min)
              {planningLoading && (
                <span className="w-3 h-3 border border-gray-300 border-t-blue-500 rounded-full animate-spin" />
              )}
            </h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <ActivityLineChart
                data={details.timeline}
                height={220}
                planning={planning}
              />
            </div>
          </div>

          {/* Activity by Tab */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <ChartBarIcon className="w-4 h-4" />
                Activité par onglet
                <span className="text-xs font-normal text-gray-400">
                  ({totalRequests} requêtes)
                </span>
              </h4>
              <button
                onClick={() => setShowFilter(!showFilter)}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                  showFilter ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                <FunnelIcon className="w-3 h-3" />
                Filtrer
              </button>
            </div>

            {/* Filter chips */}
            {showFilter && (
              <div className="flex flex-wrap gap-2 mb-3 p-3 bg-gray-50 rounded-lg">
                {details.byTab.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => toggleTab(tab.key)}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                      visibleTabs.has(tab.key)
                        ? `${tabStyles[tab.key]?.bgColor || 'bg-gray-200'} ${tabStyles[tab.key]?.color || 'text-gray-700'}`
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Tab activity bars */}
            <div className="space-y-2">
              {filteredTabs
                .sort((a, b) => b.requestCount - a.requestCount)
                .map((tab) => {
                  const maxRequests = Math.max(...filteredTabs.map(t => t.requestCount), 1);
                  const widthPercent = (tab.requestCount / maxRequests) * 100;

                  return (
                    <div key={tab.key} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${tabStyles[tab.key]?.color || 'text-gray-700'}`}>
                          {tab.label}
                        </span>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-gray-600 font-medium">{tab.requestCount}</span>
                          {tab.postCount > 0 && (
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px]">
                              +{tab.postCount}
                            </span>
                          )}
                          {tab.updateCount > 0 && (
                            <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-[10px]">
                              ~{tab.updateCount}
                            </span>
                          )}
                          {tab.errorCount > 0 && (
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px]">
                              !{tab.errorCount}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            tabStyles[tab.key]?.bgColor?.replace('100', '400') || 'bg-gray-400'
                          }`}
                          style={{
                            width: `${widthPercent}%`,
                            backgroundColor: tab.requestCount > 0 ? undefined : 'transparent'
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-3 text-[10px] text-gray-400">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-green-100 rounded" /> créations
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-yellow-100 rounded" /> modifications
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-red-100 rounded" /> erreurs
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          Aucune donnée disponible
        </div>
      )}
    </div>
  );
};
